import { Component, DestroyRef, OnInit, inject, input, output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { Subject, debounceTime, distinctUntilChanged, filter, map, merge } from 'rxjs'

@Component({
  selector: 'app-search-field',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './search-field.component.html',
  styleUrl: './search-field.component.scss'
})
export class SearchFieldComponent implements OnInit {
  public readonly placeholder = input('Search')
  public readonly widthClass = input('sm:tw-w-64')
  public readonly debounceMs = input(400)
  public readonly minChars = input(0)
  public readonly initialValue = input('')
  public readonly searchChange = output<string>()

  public readonly searchControl = new FormControl<string>('', { nonNullable: true })
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _submit$ = new Subject<string>()

  public ngOnInit(): void {
    const initial = this.initialValue()
    if (initial) this.searchControl.setValue(initial, { emitEvent: false })

    const debounced$ = this.searchControl.valueChanges.pipe(
      map((value) => value.trim()),
      debounceTime(this.debounceMs()),
      filter((value) => value.length === 0 || value.length >= this.minChars())
    )

    merge(debounced$, this._submit$)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => this.searchChange.emit(value))
  }

  /**
   * Submit the current term immediately (bypasses debounce and min-char gate).
   * @param event {Event} - The enter event.
   */
  public onEnter(event: Event): void {
    event.preventDefault()
    this._submit$.next(this.searchControl.value.trim())
  }

  /**
   * Clears the search input and emits an empty term immediately.
   */
  public clearSearch(): void {
    this.searchControl.setValue('')
    this._submit$.next('')
  }
}
